import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Truck, RefreshCw } from "lucide-react";
import { api, categoryLabel, imgProps } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";

const ASSURANCES = [
  { icon: ShieldCheck, text: "Certification documentation available for qualifying pieces" },
  { icon: Truck, text: "Insured, discreet doorstep delivery" },
  { icon: RefreshCw, text: "Guidance on care, exchange and re-setting for life" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImage(0);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        api
          .get("/products", { params: { category: r.data.category } })
          .then((res) => setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl font-light text-[#262019]">This piece has found its home</h1>
        <Link to="/collections" className="btn-gold mt-8" data-testid="not-found-back">
          Explore the Collection
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <p className="text-sm tracking-[0.2em] text-[#7C7163]">POLISHING THE DETAILS…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-8 lg:pb-32" data-testid="product-detail-page">
      <Link to="/collections" data-testid="back-to-collections" className="group flex w-fit items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">
        <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-1" />
        Back to collection
      </Link>

      <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          {product.gallery && product.gallery.length > 0 ? (
            <>
              <div className="bg-[#F4EEE3]" data-testid="product-main-image">
                <img {...imgProps(product.gallery[activeImage], "(max-width: 1024px) 100vw, 50vw")} alt={product.name} className="block h-auto w-full" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                {product.gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    data-testid={`product-thumbnail-${i}`}
                    className={`aspect-video overflow-hidden border bg-[#F4EEE3] transition-colors duration-300 ${
                      activeImage === i ? "border-[#A58343]" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img {...imgProps(img, "33vw")} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="flex aspect-video flex-col items-center justify-center gap-4 border border-[#A58343]/20 bg-[#F4EEE3]" data-testid="product-image-placeholder">
              <span className="font-serif text-7xl font-light italic text-[#A58343]/50">M</span>
              <p className="px-6 text-center text-[10px] uppercase tracking-[0.3em] text-[#7C7163]">
                Photography arriving soon
              </p>
            </div>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <p className="overline">{categoryLabel(product.category)}</p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl" data-testid="product-name">
            {product.name}
          </h1>
          <p className="mt-4 text-sm tracking-[0.2em] text-[#7C7163]" data-testid="product-sku">
            SKU {product.sku}
          </p>
          <p className="mt-6 inline-block border border-[#A58343]/30 px-5 py-2.5 text-xs uppercase tracking-[0.25em] text-[#A58343]" data-testid="product-price-note">
            Pricing shared privately in consultation
          </p>
          <p className="mt-8 text-base leading-relaxed text-[#7C7163]" data-testid="product-description">
            {product.description}
          </p>

          <dl className="mt-10 divide-y divide-[#A58343]/15 border-y border-[#A58343]/15" data-testid="product-specs">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-4">
                <dt className="text-xs uppercase tracking-[0.2em] text-[#7C7163]">{k}</dt>
                <dd className="text-right text-sm text-[#262019]">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to={`/consultation?product=${product.slug}`} className="btn-gold-solid flex-1" data-testid="product-book-consultation">
              Book a Private Consultation
            </Link>
            <Link to="/contact" className="btn-gold flex-1" data-testid="product-enquire">
              Enquire About This Piece
            </Link>
          </div>

          <div className="mt-10 space-y-4">
            {ASSURANCES.map((a) => (
              <div key={a.text} className="flex items-center gap-4">
                <a.icon size={18} strokeWidth={1.25} className="shrink-0 text-[#A58343]" />
                <p className="text-sm text-[#7C7163]">{a.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {related.length > 0 && (
        <div className="mt-32" data-testid="related-products">
          <Reveal>
            <p className="overline">Continue Exploring</p>
            <h2 className="mt-4 font-serif text-3xl font-light text-[#262019] sm:text-4xl">
              You may also <span className="italic text-[#A58343]">admire</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
